import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface RestaurantPreferenceData {
  restaurantId: string;
  diets: string[];
  allergies: string[];
  timestamp: Date;
}

export interface RestaurantAnalytics {
  restaurantId: string;
  restaurantName: string;
  dietCounts: { [diet: string]: number };
  allergyCounts: { [allergy: string]: number };
  // dietCombinationCounts: { [combination: string]: number };
  // allergyCombinationCounts: { [combination: string]: number };
  lastUpdated: Date;
  totalUsers: number;
}

export interface UserRestaurantAssociation {
  userId: string;
  lastVisitedRestaurant: string;
  visitHistory: {
    restaurantId: string;
    timestamp: Date;
  }[];
}

/**
 * Associates a user with a restaurant as their last visited
 */
export const updateUserRestaurantAssociation = async (
  userId: string,
  restaurantId: string
): Promise<void> => {
  try {
    console.log('Updating user-restaurant association for user:', userId);
    console.log('Restaurant ID:', restaurantId);
    const userRef = doc(db, 'users', userId);
    // const visitData = {
    //   restaurantId,
    //   timestamp: new Date(),
    // };

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(userRef);
      const existing = snap.data()?.visitedRestaurantIds || {};

      const updateData: Record<string, unknown> = {
        lastVisitedRestaurantId: restaurantId,
        lastVisitedAt: serverTimestamp(),
      };

      if (!existing[restaurantId]) {
        updateData['visitedRestaurantIds'] = {
          [restaurantId]: { firstVisitedAt: serverTimestamp() },
        };
      }

      transaction.set(userRef, updateData, { merge: true });
    });
  } catch (error) {
    console.error('Error updating user-restaurant association:', error);
    throw error;
  }
};

/**
 * Gets the user's last visited restaurant
 */
export const getUserLastVisitedRestaurant = async (
  userId: string
): Promise<string | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.lastVisitedRestaurantId || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting user last visited restaurant:', error);
    return null;
  }
};

/**
 * Gets restaurant analytics data
 */
export const getRestaurantAnalytics = async (
  restaurantId: string
): Promise<RestaurantAnalytics | null> => {
  try {
    const restaurantAnalyticsRef = doc(db, 'restaurantAnalytics', restaurantId);
    const restaurantDoc = await getDoc(restaurantAnalyticsRef);

    if (restaurantDoc.exists()) {
      return restaurantDoc.data() as RestaurantAnalytics;
    }
    return null;
  } catch (error) {
    console.error('Error getting restaurant analytics:', error);
    return null;
  }
};

/**
 * Generates all non-empty combinations of items from an array
 * For example: ['a', 'b'] => [['a'], ['b'], ['a', 'b']]
 */
const generateAllCombinations = (items: string[]): string[][] => {
  const combinations: string[][] = [];
  const n = items.length;

  // Generate all possible combinations using bit manipulation
  // We start from 1 to exclude empty combination
  for (let i = 1; i < 1 << n; i++) {
    const combination: string[] = [];
    for (let j = 0; j < n; j++) {
      if (i & (1 << j)) {
        combination.push(items[j]);
      }
    }
    combinations.push(combination);
  }

  return combinations;
};

/**
 * Updates restaurant analytics by querying all users who visited this restaurant
 */
export const updateRestaurantAnalytics = async (
  restaurantId: string,
  restaurantName: string
): Promise<void> => {
  try {
    // Query all users who have this restaurant as their last visited
    const usersRef = collection(db, 'users');
    const usersQuery = query(
      usersRef,
      where('lastVisitedRestaurantId', '==', restaurantId)
    );

    const usersSnapshot = await getDocs(usersQuery);

    // Aggregate all dietary preferences from these users with counts
    const dietCounts = new Map<string, number>();
    const allergyCounts = new Map<string, number>();
    // const dietCombinationCounts = new Map<string, number>();
    // const allergyCombinationCounts = new Map<string, number>();
    let totalUsers = 0;

    usersSnapshot.forEach((userDoc) => {
      const userData = userDoc.data();
      totalUsers++;

      // Count user's diets and combinations
      if (
        userData.selectedDiets &&
        Array.isArray(userData.selectedDiets) &&
        userData.selectedDiets.length > 0
      ) {
        // Count individual diets
        userData.selectedDiets.forEach((diet: string) => {
          dietCounts.set(diet, (dietCounts.get(diet) || 0) + 1);
        });

        // Generate and count all combinations for this user
        // const combinations = generateAllCombinations(userData.selectedDiets);
        // combinations.forEach((combination) => {
        //   // Sort the combination for consistent key format
        //   const sortedCombination = combination.sort();
        //   const combinationKey = sortedCombination.join(', ');
        //   // dietCombinationCounts.set(
        //   //   combinationKey,
        //   //   (dietCombinationCounts.get(combinationKey) || 0) + 1
        //   // );
        // });
      }

      // Count user's allergies and combinations
      if (
        userData.selectedAllergies &&
        Array.isArray(userData.selectedAllergies) &&
        userData.selectedAllergies.length > 0
      ) {
        // Count individual allergies
        userData.selectedAllergies.forEach((allergy: string) => {
          allergyCounts.set(allergy, (allergyCounts.get(allergy) || 0) + 1);
        });

        // Generate and count all combinations for this user
        // const combinations = generateAllCombinations(
        //   userData.selectedAllergies
        // );
        // combinations.forEach((combination) => {
        //   // Sort the combination for consistent key format
        //   const sortedCombination = combination.sort();
        //   const combinationKey = sortedCombination.join(', ');
        //   allergyCombinationCounts.set(
        //     combinationKey,
        //     (allergyCombinationCounts.get(combinationKey) || 0) + 1
        //   );
        // });
      }
    });

    // Update restaurant analytics with aggregated data
    const restaurantAnalyticsRef = doc(db, 'restaurantAnalytics', restaurantId);
    const analyticsData: RestaurantAnalytics = {
      restaurantId,
      restaurantName,
      dietCounts: Object.fromEntries(dietCounts),
      allergyCounts: Object.fromEntries(allergyCounts),
      // dietCombinationCounts: Object.fromEntries(dietCombinationCounts),
      // allergyCombinationCounts: Object.fromEntries(allergyCombinationCounts),
      lastUpdated: new Date(),
      totalUsers,
    };

    await setDoc(restaurantAnalyticsRef, analyticsData);

    console.log(`Updated analytics for restaurant ${restaurantId}:`, {
      totalUsers,
      dietTypes: Object.keys(analyticsData.dietCounts).length,
      allergyTypes: Object.keys(analyticsData.allergyCounts).length,
      dietCounts: analyticsData.dietCounts,
      allergyCounts: analyticsData.allergyCounts,
      // dietCombinations: Object.keys(analyticsData.dietCombinationCounts).length,
      // dietCombinationCounts: analyticsData.dietCombinationCounts,
      // allergyCombinations: Object.keys(analyticsData.allergyCombinationCounts).length,
      // allergyCombinationCounts: analyticsData.allergyCombinationCounts,
    });
  } catch (error) {
    console.error('Error updating restaurant analytics:', error);
    throw error;
  }
};

/**
 * Records user preference changes and updates restaurant analytics
 */
export const recordUserPreferenceChange = async (
  userId: string,
  restaurantId: string,
  restaurantName: string,
  diets: string[],
  allergies: string[],
  changeType: 'add' | 'remove' | 'update'
): Promise<void> => {
  try {
    // Update user-restaurant association
    await updateUserRestaurantAssociation(userId, restaurantId);

    // Recalculate restaurant analytics from all users
    await updateRestaurantAnalytics(restaurantId, restaurantName);

    console.log(
      `Recorded preference change for user ${userId} at restaurant ${restaurantId}`
    );
  } catch (error) {
    console.error('Error recording user preference change:', error);
    throw error;
  }
};

/**
 * Bulk update analytics for all restaurants (useful for data migrations or cleanup)
 */
export const recalculateAllRestaurantAnalytics = async (): Promise<void> => {
  try {
    // Get all unique restaurants from user associations
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);

    const restaurantIds = new Set<string>();
    const restaurantNames = new Map<string, string>();

    usersSnapshot.forEach((userDoc) => {
      const userData = userDoc.data();
      if (userData.lastVisitedRestaurantId) {
        restaurantIds.add(userData.lastVisitedRestaurantId);
        // You might need to fetch restaurant names separately if not stored in user data
      }
    });

    // Update analytics for each restaurant
    for (const restaurantId of Array.from(restaurantIds)) {
      // You'll need to fetch the restaurant name from your restaurants collection
      const restaurantName =
        restaurantNames.get(restaurantId) || 'Unknown Restaurant';
      await updateRestaurantAnalytics(restaurantId, restaurantName);
    }

    console.log(`Recalculated analytics for ${restaurantIds.size} restaurants`);
  } catch (error) {
    console.error('Error recalculating all restaurant analytics:', error);
    throw error;
  }
};
